import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'motion/react';
import { z } from 'zod';
import { toast } from 'sonner';

import { signInSchema, type SignInInput } from '@sreerasa/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Logo } from '@/components/brand/logo';
import { supabase } from '@/lib/supabase';
import { useCart } from '@/features/cart/cart-store';
import { transitions } from '@/components/motion/primitives';

export const Route = createFileRoute('/auth/sign-in')({
  validateSearch: z.object({ redirect: z.string().optional() }),
  component: SignInPage,
});

function SignInPage() {
  const { redirect } = Route.useSearch();
  const navigate = useNavigate();
  const hydrateCart = useCart((state) => state.hydrateFromServer);

  const form = useForm<SignInInput>({
    resolver: zodResolver(signInSchema),
    defaultValues: { email: '', password: '' },
  });

  async function onSubmit(values: SignInInput) {
    const { error } = await supabase.auth.signInWithPassword(values);

    if (error) {
      // Supabase returns a deliberately vague message here so an attacker
      // can't enumerate which emails have accounts. Pass it through rather
      // than "helpfully" narrowing it.
      form.setError('root', { message: error.message });
      return;
    }

    // Merge the guest cart into the server cart now that we know who they are.
    await hydrateCart();
    toast.success('Welcome back');
    void navigate({ to: redirect ?? '/' });
  }

  return (
    <div className="mx-auto flex min-h-[80vh] max-w-md flex-col justify-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={transitions.base}
      >
        <div className="text-center">
          <Link to="/" className="inline-block">
            <Logo size="lg" />
          </Link>
          <h1 className="mt-6 font-display text-3xl font-black text-soil-900">Welcome back</h1>
          <p className="mt-2 text-soil-600">Sign in to track orders and check out faster.</p>
        </div>

        <form noValidate onSubmit={form.handleSubmit(onSubmit)} className="mt-8 space-y-4">
          <Input
            label="Email"
            type="email"
            autoComplete="email"
            required
            {...form.register('email')}
            error={form.formState.errors.email?.message}
          />
          <Input
            label="Password"
            type="password"
            autoComplete="current-password"
            required
            {...form.register('password')}
            error={form.formState.errors.password?.message}
          />

          {form.formState.errors.root && (
            <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm font-medium text-danger">
              {form.formState.errors.root.message}
            </p>
          )}

          <Button
            type="submit"
            size="lg"
            full
            isLoading={form.formState.isSubmitting}
            loadingText="Signing in…"
          >
            Sign in
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-soil-600">
          New here?{' '}
          <Link to="/auth/sign-up" className="font-semibold text-farm-700 hover:underline">
            Create an account
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
